import type { AnyAction, Reducer } from '@reduxjs/toolkit';
import { isEqual } from 'lodash-es';
import {
  ADD_STORAGE_TO_SCREEN_OUTPUTS,
  ADD_UNIT,
  EDIT_FLOW_PROPERTIES,
  EDIT_LAYOUT,
  EDIT_LAYOUTS,
  EDIT_MULTIPLE_UNIT_LOCATIONS,
  EDIT_PORT,
  EDIT_UNIT_FLOW,
  EDIT_UNIT_INFO,
  INSERT_ON_CONNECTION,
  REMOVE_UNIT,
  REPLACE_UNIT,
  TOGGLE_MIRRORING,
} from './units/types';
import {
  ADD_CONNECTION,
  EDIT_CONNECTION,
  REMOVE_CONNECTION,
  SET_CONNECTION_COLOR,
  UPDATE_CONNECTION_POINTS,
} from './connections/types';
import type { ConnectionInterface } from './connections/types';
import type { UnitInterface } from './units/types';

export interface StateWithHistory<State> {
  past: State[];
  present: State;
  future: State[];
}

export const UNDO = 'history/undo';
export const REDO = 'history/redo';
const CLEAR_HISTORY = 'history/clear';

const HISTORY_LIMIT = 50;

const getUnitConnState = (state: unknown) => {
  if (
    typeof state === 'object' &&
    state !== null &&
    'unitState' in state &&
    'connectionState' in state
  ) {
    return state as {
      unitState: {
        units: UnitInterface[];
        panels: Record<string, unknown>;
        groups: Record<string, unknown>;
      };
      connectionState: {
        connections: ConnectionInterface[];
      };
    };
  }

  return null;
};

const shouldRecordUnchangedSagaAction = (
  present: unknown,
  action: AnyAction,
) => {
  const state = getUnitConnState(present);

  if (!state) {
    return false;
  }

  const units = state.unitState.units;
  const connections = state.connectionState.connections;
  const unitsByKey = new Map(units.map((unit) => [unit.key, unit]));
  const unitByKey = (key: string | null | undefined) =>
    key ? unitsByKey.get(key) : undefined;

  switch (action.type) {
    case ADD_CONNECTION: {
      const portKeys = action.payload?.portkeys;
      return (
        !!portKeys &&
        !connections.some(
          (connection) =>
            connection.fromPort === portKeys.fromPort &&
            connection.toPort === portKeys.toPort,
        )
      );
    }
    case REMOVE_CONNECTION: {
      const portKeysToRemove = Array.isArray(action.payload)
        ? action.payload
        : action.payload
          ? [action.payload]
          : [];
      return connections.some((connection) =>
        portKeysToRemove.some(
          (portKeys: { fromPort: string; toPort: string }) =>
            connection.fromPort === portKeys.fromPort &&
            connection.toPort === portKeys.toPort,
        ),
      );
    }
    case EDIT_CONNECTION: {
      return (action.payload ?? []).some(
        (payload: Partial<ConnectionInterface>) => {
          const connection = connections.find(
            (connection) =>
              connection.fromPort === payload.fromPort ||
              connection.toPort === payload.toPort,
          );

          return (
            connection &&
            (!isEqual(connection.fromPort, payload.fromPort) ||
              !isEqual(connection.toPort, payload.toPort) ||
              !isEqual(connection.from, payload.from) ||
              !isEqual(connection.to, payload.to) ||
              !isEqual(connection.flowRatePosition, payload.flowRatePosition))
          );
        },
      );
    }
    case UPDATE_CONNECTION_POINTS: {
      const payload = action.payload?.updatedConnectionPointsPayload;
      const connection = connections.find(
        (connection) => connection.key === payload?.connectionKey,
      );

      return (
        !!connection &&
        !isEqual(connection.connectionPoints, payload?.updateConnectionPoints)
      );
    }
    case SET_CONNECTION_COLOR: {
      const connectionKeys = action.payload?.connectionKeys ?? [];

      return connections.some(
        (connection) =>
          connectionKeys.includes(connection.key) &&
          connection.color !== action.payload?.color,
      );
    }
    case ADD_UNIT:
      return true;
    case EDIT_UNIT_INFO: {
      const unit = unitByKey(action.payload?.unitKey);
      return !!unit && !isEqual(unit.unitInfo, action.payload?.unitInfo);
    }
    case EDIT_UNIT_FLOW: {
      const unit = unitByKey(action.payload?.unitKey);
      return (
        !!unit && !isEqual(unit.unitInfo?.inputFlow, action.payload?.inputFlow)
      );
    }
    case EDIT_FLOW_PROPERTIES: {
      const unit = unitByKey(action.payload?.unitKey);
      return (
        !!unit && !isEqual(unit.flowProperties, action.payload?.flowProperties)
      );
    }
    case EDIT_LAYOUT: {
      const unit = unitByKey(action.payload?.unitKey);
      return !!unit && !isEqual(unit.layout, action.payload?.layout);
    }
    case EDIT_LAYOUTS: {
      return (action.payload ?? []).some(
        (payload: { unitKey?: string; layout?: unknown }) => {
          const unit = unitByKey(payload.unitKey);
          return !!unit && !isEqual(unit.layout, payload.layout);
        },
      );
    }
    case EDIT_MULTIPLE_UNIT_LOCATIONS: {
      return (action.payload?.unitLocationsToEdit ?? []).some(
        (payload: { unitKey?: string; location?: string }) => {
          const unit = unitByKey(payload.unitKey);
          return !!unit && unit.layout?.loc !== payload.location;
        },
      );
    }
    case EDIT_PORT: {
      const unit = unitByKey(action.payload?.unitKey);
      return !!unit && action.payload?.diff !== 0;
    }
    case REMOVE_UNIT:
      return (action.payload ?? []).some((key: string) => !!unitByKey(key));
    case REPLACE_UNIT: {
      const unit = unitByKey(action.payload?.key);
      return (
        !!unit &&
        (unit.type !== action.payload?.type ||
          unit.layout?.variantId !== action.payload?.variantId)
      );
    }
    case TOGGLE_MIRRORING:
      return !!unitByKey(action.payload?.unitKey);
    case INSERT_ON_CONNECTION:
      return connections.some(
        (connection) => connection.key === action.payload?.connection?.key,
      );
    case ADD_STORAGE_TO_SCREEN_OUTPUTS:
      return true;
    default:
      return false;
  }
};

const shouldRecordHistory = <T>(present: T, newPresent: T, action: AnyAction) =>
  present !== newPresent || shouldRecordUnchangedSagaAction(present, action);

export const HistoryActions = {
  undo: () => ({ type: UNDO }),
  redo: () => ({ type: REDO }),
  clearHistory: () => ({ type: CLEAR_HISTORY }),
};

/**
 *
 * @param reducer
 * @param undoableActions list of actions that should be included in the history
 * @returns an reducer with undo/redo functionality
 */
export const createUndoableReducer = <T>(
  reducer: Reducer<T>,
  undoableActions: string[],
) => {
  const initialState: StateWithHistory<T> = {
    past: [],
    present: reducer(undefined, {} as AnyAction),
    future: [],
  };

  return function (
    state = initialState,
    action: AnyAction,
  ): StateWithHistory<T> {
    const { present, past, future } = state;
    const newPresent = reducer(state.present, action);

    switch (action.type) {
      case UNDO: {
        let meaningfulPast = past;
        let skippedNoOpSnapshots = false;
        while (
          meaningfulPast.length > 0 &&
          meaningfulPast[meaningfulPast.length - 1] === present
        ) {
          meaningfulPast = meaningfulPast.slice(0, -1);
          skippedNoOpSnapshots = true;
        }

        if (meaningfulPast.length === 0) {
          return skippedNoOpSnapshots
            ? {
                ...state,
                past: meaningfulPast,
              }
            : state;
        }
        const previous = meaningfulPast[meaningfulPast.length - 1];
        const newPast = meaningfulPast.slice(0, meaningfulPast.length - 1);

        return {
          past: newPast,
          present: previous,
          future: [present, ...future],
        };
      }
      case REDO: {
        let meaningfulFuture = future;
        let skippedNoOpSnapshots = false;
        while (meaningfulFuture.length > 0 && meaningfulFuture[0] === present) {
          meaningfulFuture = meaningfulFuture.slice(1);
          skippedNoOpSnapshots = true;
        }

        if (meaningfulFuture.length === 0) {
          return skippedNoOpSnapshots
            ? {
                ...state,
                future: meaningfulFuture,
              }
            : state;
        }
        const next = meaningfulFuture[0];
        const newFuture = meaningfulFuture.slice(1);

        return {
          past: [...past, present],
          present: next,
          future: newFuture,
        };
      }
      case CLEAR_HISTORY: {
        return {
          ...state,
          past: [],
          present: newPresent,
          future: [],
        };
      }
      default: {
        if (!undoableActions.includes(action.type)) {
          return {
            ...state,
            present: newPresent,
          };
        }

        if (!shouldRecordHistory(present, newPresent, action)) {
          return {
            ...state,
            present: newPresent,
          };
        }

        return {
          past: [...state.past, present].slice(-HISTORY_LIMIT),
          present: newPresent,
          future: [],
        };
      }
    }
  };
};
